import { sortDefault as defaultSortFunction } from 'src/Helpers/TableHelpers'
import { IColumn } from 'src/Models/Client'

export interface IState {
  page: number
  pageSize: number
  sorting?: Sorting
  allRows: any[]
  filteredRows: any[]
  columns: IColumn[]
  pagedRows: any[]
  serverPaging?: ServerPaging
  totalRows: number
  filter?: Filter
  selectIdDataColumn?: string
  selectedRowIds: any[]
}

export const initialState: IState = {
  page: 1,
  pageSize: 10,
  allRows: [],
  filteredRows: [],
  pagedRows: [],
  columns: [],
  totalRows: 1,
  selectedRowIds: [],
}

export type Sorting = {
  dataField: string
  order: 'asc' | 'desc'
}

export type Filter = {
  dataField: string
  filter: string
}

export type ServerPaging = {
  page: number
  pageSize: number
  totalRows: number
  onPagination: (page: number, pageSize) => void
}

export type Actions =
  | { type: 'setPage'; newPage: number }
  | { type: 'setPageSize'; newPageSize: number }
  | { type: 'setSorting'; newSorting: Sorting }
  | {
      type: 'initializePage'
      allRows: any[]
      columns: IColumn[]
      serverPaging?: ServerPaging
      defaultSorting?: Sorting
      defaultPageSize: number
      selectIdDataColumn?: string
    }
  | { type: 'setFilter'; newFilter: Filter }
  | { type: 'setSelectedRowIds'; selectedRowIds: any[] }

export function reducer(state: IState, action: Actions): IState {
  let newState: IState
  //console.log('action', action.type)
  switch (action.type) {
    case 'initializePage':
      newState = {
        ...state,
        allRows: action.allRows,
        columns: action.columns,
        filteredRows: action.allRows,
        sorting: action.defaultSorting,
        totalRows:
          action.serverPaging !== undefined
            ? action.serverPaging.totalRows
            : action.allRows.length,
        pageSize: action.defaultPageSize,
        selectIdDataColumn: action.selectIdDataColumn,
      }

      //default sort
      newState.pagedRows = sortAndPageRows(newState)
      break
    case 'setSelectedRowIds':
      return { ...state, selectedRowIds: action.selectedRowIds }
    case 'setPage':
      newState = {
        ...state,
        page: action.newPage,
      }

      newState.pagedRows = sortAndPageRows(newState)
      break
    case 'setPageSize':
      newState = {
        ...state,
        page: 1,
        pageSize: action.newPageSize,
      }
      newState.pagedRows = sortAndPageRows(newState)
      break
    case 'setSorting':
      newState = {
        ...state,
        page: 1,
        sorting: action.newSorting,
      }
      newState.pagedRows = sortAndPageRows(newState)
      break
    case 'setFilter': {
      newState = {
        ...state,
        page: 1,
        filter: action.newFilter,
      }

      const { allRows, filter } = newState
      let filteredRows = allRows
      if (filter.filter.trim() != '') {
        filteredRows = allRows.filter((row) => {
          const value = row[`${filter.dataField}`] as string
          if (value !== undefined) {
            return value.toLowerCase().indexOf(filter.filter.toLowerCase()) > -1
          }
          return false
        })
      }
      newState.filteredRows = filteredRows
      newState.totalRows = filteredRows.length
      newState.pagedRows = sortAndPageRows(newState)
      break
    }
    default:
      throw new Error('Invalid Reducer Action')
  }
  return newState
}

const sortAndPageRows = (state: IState) => {
  const {
    allRows,
    filteredRows,
    page,
    pageSize,
    sorting,
    columns,
    serverPaging,
  } = state

  if (serverPaging) {
    return allRows
  }

  //clientside use filterRows
  if (sorting) {
    const sortedColumn = columns.find((x) => x.dataField === sorting.dataField)

    //custom sort
    if (sortedColumn !== undefined && sortedColumn.sortFunc !== undefined) {
      filteredRows.sort((a, b) =>
        sortedColumn.sortFunc(sorting.order, sorting.dataField, a, b)
      )
    } else {
      filteredRows.sort((a, b) =>
        defaultSortFunction(sorting.order, sorting.dataField, a, b)
      )
    }
  }

  //client paging
  return filteredRows.slice((page - 1) * pageSize, page * pageSize)
}

export const reducerActions = (
  state: IState,
  dispatch: (action: Actions) => void
) => {
  return {
    initializePage: (
      data: any[],
      columns: IColumn[],
      defaultPageSize: number,
      serverPaging?: ServerPaging,
      defaultSorting?: Sorting,
      selectIdDataColumn?: string
    ) => {
      dispatch({
        type: 'initializePage',
        allRows: data,
        columns,
        serverPaging,
        defaultSorting,
        defaultPageSize,
        selectIdDataColumn,
      })
    },
    setSorting: (newSorting: Sorting) => {
      dispatch({ type: 'setSorting', newSorting })
    },
    setFilter: (filter: string, column: IColumn) => {
      dispatch({
        type: 'setFilter',
        newFilter: { filter, dataField: column.dataField },
      })
    },

    setPage: (newPage: number) => {
      dispatch({ type: 'setPage', newPage })
    },
    setPageSize: (newPageSize: number) => {
      dispatch({ type: 'setPageSize', newPageSize })
    },
    setRowSelection: (
      checked: boolean,
      row: any,
      onSelect?: (selectedIds: any[]) => void
    ) => {
      const rowId = row[state.selectIdDataColumn]

      if (checked) {
        //add
        const selectedRowIds = [...state.selectedRowIds, rowId]
        dispatch({ type: 'setSelectedRowIds', selectedRowIds })
        onSelect(selectedRowIds)
      } else {
        //remove
        const rowIndex = state.selectedRowIds.findIndex((x) => x === rowId)
        if (rowIndex > -1) {
          const selectedRowIds = [...state.selectedRowIds]
          selectedRowIds.splice(rowIndex, 1)
          dispatch({ type: 'setSelectedRowIds', selectedRowIds })
          onSelect(selectedRowIds)
        }
      }
    },

    setSelectAll: (
      checked: boolean,
      onSelect?: (selectedIds: any[]) => void
    ) => {
      const selectedRowIds = state.allRows.map(
        (row) => row[state.selectIdDataColumn]
      )
      if (checked) {
        dispatch({ type: 'setSelectedRowIds', selectedRowIds })
        onSelect(selectedRowIds)
      } else {
        //deselect all
        dispatch({ type: 'setSelectedRowIds', selectedRowIds: [] })
        onSelect([])
      }
    },
  }
}
